import { useEffect, useRef, useState } from 'react';
import {
  App as AntApp,
  Button,
  Form,
  Input,
  InputNumber,
  Segmented,
  Spin,
  Tag
} from 'antd';
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  CrownOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  LockOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SettingOutlined,
  StarFilled,
  TeamOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { getSignupState, patchSignupState } from '../../contexts/signupStore';
import { paiseToRupees } from '../../utils/format';
import { parseApiError } from '../../utils/notify';
import { isMasterUsername, passwordStrength } from '../../utils/validators';
import {
  describeCheckoutError,
  openRazorpayCheckout
} from '../../utils/razorpay';

// Inline-SVG leaf icon for the Trial Plan card. Drawn locally so we
// don't pull in any external icon set beyond @ant-design/icons (which
// has no leaf glyph). Sized via the wrapping span; viewBox handles
// the scaling.
function LeafIcon({ className = '' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M5 19c0-7 5-13 14-14 0 9-6 14-13 14a4 4 0 0 1-1 0Z" />
      <path d="M5 19c2-3 4-5 8-7" />
    </svg>
  );
}

// Single-page Razorpay signup-payment surface.
//
// This page owns the merged plan-selection (was /signup/quote) and the
// payment + master-credentials step. It implements an explicit phase state
// machine so the UI never gets stuck in an ambiguous in-flight state.
//
// PHASES
//   initializing         — first render before sessionStorage is hydrated
//   session_expired      — required signup state missing; bouncing to /signup
//   choosing_plan        — quote not yet fetched; plan form is interactive
//   fetching_quote       — POST /signup/quote in flight
//   quote_ready          — quote in store; credentials form is interactive
//   creating_order       — POST /signup/create-order in flight
//   checkout_opened      — Razorpay modal is displayed; user is paying
//   payment_cancelled    — Razorpay modal dismissed by user; safe to retry
//   payment_failed       — Razorpay-side failure; safe to retry
//   verification_pending — POST /signup/verify-payment in flight
//   verification_slow    — verify-payment > 10s; same call still in flight
//   verification_failed  — backend rejected the verify; signaturePayload is
//                          held in component state so user can retry safely
//   verified             — success acknowledged, about to navigate
//   completed            — navigated; component will unmount
//
// The Pay button is disabled in any phase where another action is active
// (creating_order, checkout_opened, verification_pending, verification_slow,
// verified, completed). It is re-enabled in choosing_plan, quote_ready,
// payment_cancelled, payment_failed, verification_failed.
//
// Critical safety properties (per spec):
//   - We NEVER provision tenant based on the Razorpay handler alone. The
//     handler resolution simply hands us the (paymentId, orderId, signature)
//     triple; the actual provisioning gate is /signup/verify-payment which
//     re-verifies HMAC server-side and re-fetches the payment from Razorpay.
//   - On verification failure with a known triple, the user can retry
//     /signup/verify-payment without going back to Razorpay. The backend's
//     idempotent fast-path means the retry never double-provisions.
//   - inFlightRef is a ref-level lock that survives React re-renders and
//     races; double-clicks during the same async chain hit a popup, not a
//     duplicate Razorpay order.
const SAFE = (s) => (s == null ? '' : String(s));
const SLOW_MS = 10_000;

// Stage 5K-4A: Free Trial plan presentation. Server is the source of
// truth for everything provisioned (`/api/signup/activate-free-trial`
// returns the actual trial-end / included-users values from the
// freshly-inserted subscription). These constants only drive the
// pre-activation card UI + the safe sessionStorage state used for
// refresh recovery — they're never persisted, signed, or sent back to
// the backend, so a stale value here can't bypass any server check.
// Stage 5K-4B: paid trial plan card metadata. Display-only — the
// authoritative pricing comes from the backend's /signup/quote
// response when the Trial card is clicked, so a stale value here
// cannot influence what Razorpay charges. The user pays exactly
// what the backend returns, which is recomputed from the
// purchase_plans.TRIAL_7D_99 row server-side.
const TRIAL_PLAN = Object.freeze({
  planCode: 'TRIAL_7D_99',
  planName: 'Trial Plan',
  includedUsers: 5,
  trialDays: 7,
  displayPrice: '₹99',
  displayPriceNote: 'including GST',
  requiresPayment: true
});

const ACTIVE_PHASES = new Set([
  'fetching_quote',
  'creating_order',
  'checkout_opened',
  'verification_pending',
  'verification_slow',
  'verified',
  'completed'
]);

// Phases where a tab close / refresh would be destructive: payment is
// either being created or being verified. We attach a beforeunload guard
// only during these so that idle states (quote_ready, payment_cancelled,
// payment_failed, verification_failed, completed) don't bother the user
// with the browser's confirmation dialog.
const RISKY_PHASES = new Set([
  'creating_order',
  'checkout_opened',
  'verification_pending',
  'verification_slow'
]);

// Stage 5J-D: client-side attempt id used by the backend to dedupe
// create-order calls across refreshes/double-clicks. Persisted to
// sessionStorage as part of glassi.signup so the same browser session
// reuses it. Generated fresh when missing or after "Start fresh payment".
//
// crypto.randomUUID() produces a 36-char [0-9a-f-] string which fits the
// backend's [A-Za-z0-9_-]{20,100} constraint. Fallback for the rare older
// browsers without randomUUID is a v4-shaped synthetic id.
function generateClientAttemptId() {
  try {
    if (
      typeof crypto !== 'undefined' &&
      crypto &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
  } catch (_e) {
    /* fall through */
  }
  // Minimal fallback — only hit on very old browsers without crypto.randomUUID.
  // Format matches the constraint; randomness is acceptable for dedup, not
  // crypto security.
  const rand = () =>
    Math.floor(Math.random() * 0xffffffff)
      .toString(16)
      .padStart(8, '0');
  return [rand(), rand().slice(0, 4), '4' + rand().slice(0, 3), rand().slice(0, 4), rand() + rand().slice(0, 4)].join(
    '-'
  );
}

export default function SignupPaymentPage() {
  const navigate = useNavigate();
  const { notification, modal } = AntApp.useApp();

  const [phase, setPhase] = useState('initializing');
  const [pendingSignupId, setPendingSignupId] = useState(null);
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);

  // After a successful Razorpay handler, the signed triple is held here so
  // a verification failure can be retried without opening Razorpay again.
  // It lives ONLY in React state — never persisted, lost on refresh/close
  // by design (signatures are short-lived secrets).
  const [lastSignature, setLastSignature] = useState(null);
  // Master credentials — held in a ref (not React state) so they aren't
  // serialized on every re-render and aren't kept in sessionStorage. Cleared
  // on success / on Back-to-plans / on unmount.
  const heldCredentialsRef = useRef(null);
  // Recovered username (from store) — purely for prefilling the form on
  // refresh. Password is NEVER prefilled; user must always re-enter it.
  const [recoveredUsername, setRecoveredUsername] = useState('');
  // True when this page mount picked up an existing order from store —
  // i.e. the user is resuming a previous payment attempt rather than
  // starting a fresh one. Drives the recovery banner.
  const [resumedAttempt, setResumedAttempt] = useState(false);
  // Stable client_attempt_id sent with every create-order. Persisted in
  // signupStore so refresh + back-button reuse the same id and the backend
  // dedupes via the partial unique index.
  const [clientAttemptId, setClientAttemptId] = useState('');

  // Atomic single-flight guard. Refs don't race with React state updates,
  // so two rapid Pay clicks during a single render-tick still see the same
  // truthy ref and the second one is rejected.
  const inFlightRef = useRef(false);
  const slowTimerRef = useRef(null);

  // Live order-summary preview for the plan-selection step. We refresh
  // this from the backend's /signup/quote whenever the user changes
  // userCount or billingCycle so the total + GST + discount the user is
  // about to commit to is visible BEFORE they click Continue. The
  // backend remains the source of truth — we never compute price on the
  // client. `livePreview` is intentionally separate from the locked
  // `quote` state: the locked quote is what gets sent to /create-order.
  const [livePreview, setLivePreview] = useState(null);
  const [livePreviewLoading, setLivePreviewLoading] = useState(false);
  const livePreviewTimerRef = useRef(null);

  // Stage 5N-4: load the live Standard Plan pricing config so the plan
  // card doesn't hardcode ₹1,999 / 18% / annual benefit copy. Falls
  // back to a sensible default if the endpoint is unavailable.
  const [pricingConfig, setPricingConfig] = useState(null);
  // Stage 5N-13: trial-eligibility hint from /signup/status. When the
  // GSTIN already owns a tenant the backend will reject the trial
  // quote with a 409. Reading the hint up-front lets us disable the
  // Trial card BEFORE the user clicks instead of showing a scary
  // popup on click. Backend re-checks at quote time — never relied
  // on as the only enforcement.
  //   trialEligibility = { eligible: bool, reason: string|null }
  const [trialEligibility, setTrialEligibility] = useState({
    eligible: true,
    reason: null
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await endpoints.signup.pricingConfig();
        if (!cancelled) setPricingConfig(cfg);
      } catch (_e) {
        // Render with the legacy hardcoded copy if the read fails —
        // signup must remain usable even if the read endpoint blips.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Forms — kept separate so resetting one doesn't disturb the other.
  const [planForm] = Form.useForm();
  const [credForm] = Form.useForm();

  // Stage 5K-4B + 5K-CustomPlan: plan-picker view machine. The Plan
  // step has three sub-views:
  //   'cards'              — initial picker with three simple cards
  //                          (Trial Plan ₹99 / Standard Plan / Custom Plan)
  //   'configure_standard' — user count + billing cycle + order summary
  //                          + Continue to payment, shown after the
  //                          Standard card's "Configure standard plan"
  //                          button is clicked.
  //   'custom_code'        — single-input panel for entering a Custom
  //                          Plan code. Shown after clicking the
  //                          Custom Plan card. Backend resolves the
  //                          code to a payment-required custom plan;
  //                          there is no public no-payment path.
  // The Trial card skips configure entirely — it fetches a trial quote
  // with planCode='TRIAL_7D_99' and transitions phase directly to
  // 'quote_ready' so the existing credentials + Razorpay surface kicks
  // in for the paid ₹99 trial.
  const [planView, setPlanView] = useState('cards');

  // Stage 5K-CustomPlan: custom-code input state. Lives only inside the
  // custom_code sub-view; wiped after a successful apply. The applied
  // quote (if any) is stored on the shared `quote` state so the
  // existing credentials surface picks it up.
  const [customCodeInput, setCustomCodeInput] = useState('');
  const [customCodeBusy, setCustomCodeBusy] = useState(false);

  // Watch the plan form so the live preview can refresh on every change.
  // These hooks must be declared at the top level — moving them next to
  // the effect that uses them would only run them when the component
  // re-rendered for an unrelated reason.
  const watchedUserCount = Form.useWatch('userCount', planForm);
  const watchedBillingCycle = Form.useWatch('billingCycle', planForm);
  // Live master-password value so the requirement chips below the
  // password field can render as "satisfied" the moment each rule
  // becomes true. Display-only — the actual gate is the existing
  // passwordStrength() Form rule, which still runs on submit.
  const watchedMasterPassword = Form.useWatch('masterPassword', credForm);

  // ---------------- popup helpers ----------------
  function popupError(title, content) {
    modal.error({ title, content, okText: 'OK', maskClosable: true });
  }
  function popupWarn(title, content) {
    notification.warning({
      message: title,
      description: content,
      placement: 'topRight',
      duration: 6
    });
  }
  function popupInfo(title, content) {
    notification.info({
      message: title,
      description: content,
      placement: 'topRight',
      duration: 4
    });
  }
  function popupSuccess(title, content) {
    notification.success({
      message: title,
      description: content,
      placement: 'topRight',
      duration: 4
    });
  }

  // ---------------- mount / hydration ----------------
  useEffect(() => {
    const fresh = getSignupState();
    const psid = fresh.pendingSignupId;

    if (!psid) {
      // No signup in progress — surface a popup, then bounce to /signup.
      setPhase('session_expired');
      modal.warning({
        title: 'Signup payment session expired',
        content:
          'We could not find your in-progress signup. Please start again from the beginning.',
        okText: 'Restart signup',
        // Lock the modal so users can't dismiss it and end up stranded on
        // the loading spinner — the only exit is the OK button which
        // navigates them back to the signup start.
        closable: false,
        keyboard: false,
        maskClosable: false,
        onOk: () => navigate('/signup', { replace: true })
      });
      return;
    }

    setPendingSignupId(psid);
    setQuote(fresh.quote || null);
    setOrder(fresh.order || null);
    setRecoveredUsername(SAFE(fresh.masterUsername));

    // Hydrate or mint the client_attempt_id. Reuse what's in the store so
    // refresh-and-retry hits the backend dedup path; mint a new one only
    // when the store is empty (first ever visit) or has been cleared by
    // "Start fresh payment".
    let aid = SAFE(fresh.clientAttemptId);
    if (!aid || aid.length < 20) {
      aid = generateClientAttemptId();
      patchSignupState({ clientAttemptId: aid });
    }
    setClientAttemptId(aid);
    // If we hydrated a quote AND/OR an order from sessionStorage, this is
    // a resumed attempt (refresh, browser back-forward, second tab in same
    // session, etc). Show the recovery banner + a one-shot notification so
    // the user understands their state was rebuilt and the password field
    // was deliberately cleared.
    const hadQuote = Boolean(fresh.quote);
    const hadOrder = Boolean(fresh.order && fresh.order.razorpayOrderId);
    if (hadQuote || hadOrder) {
      setResumedAttempt(hadOrder);
      notification.info({
        message: 'Payment page recovered',
        description:
          'Your plan' +
          (hadOrder ? ' and pending order' : '') +
          ' were restored. Please re-enter your master password to continue.',
        placement: 'topRight',
        duration: 6
      });
    }
    setPhase(fresh.quote ? 'quote_ready' : 'choosing_plan');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // beforeunload guard. Browsers ignore custom messages now (returnValue
  // = '' or e.preventDefault()), but they still show their generic
  // "Leave site? Changes you made may not be saved" prompt — which is the
  // right UX for an in-flight payment. Only attach during RISKY_PHASES so
  // we don't pester users in idle states.
  useEffect(() => {
    if (!RISKY_PHASES.has(phase)) return undefined;
    function handler(e) {
      // Required to trigger the browser dialog.
      e.preventDefault();
      e.returnValue = '';
      return '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [phase]);

  // ---------------- slow-verification timer ----------------
  useEffect(() => {
    function clear() {
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
        slowTimerRef.current = null;
      }
    }
    if (phase === 'verification_pending') {
      slowTimerRef.current = setTimeout(() => {
        setPhase((p) => (p === 'verification_pending' ? 'verification_slow' : p));
        notification.warning({
          message: 'Verification is taking longer than expected',
          description:
            'Please do not pay again. You can retry verification using the same payment reference.',
          placement: 'topRight',
          duration: 0
        });
      }, SLOW_MS);
    } else {
      clear();
    }
    return clear;
  }, [phase, notification]);

  // Clear held credentials on unmount so they never leak into another page.
  useEffect(() => {
    return () => {
      heldCredentialsRef.current = null;
    };
  }, []);

  // Stage 5N-13: fetch trial eligibility once we know the pendingSignupId.
  // Best-effort — silent fallback to "eligible" so a status-endpoint
  // hiccup doesn't accidentally hide a working trial. The backend
  // remains authoritative at quote time.
  useEffect(() => {
    if (!pendingSignupId) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await endpoints.signup.status(pendingSignupId);
        if (cancelled) return;
        if (status && status.trialEligibility) {
          setTrialEligibility({
            eligible: status.trialEligibility.eligible !== false,
            reason: status.trialEligibility.reason || null
          });
        }
      } catch (_e) {
        // Silent — handler in handleStartTrial catches the 409 if the
        // hint was wrong / unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingSignupId]);

  // ---------------- live order-summary preview ----------------
  // While the user is editing the plan (no locked quote yet), refresh
  // the order summary 400ms after the last keystroke / radio change.
  // We deliberately swallow errors here — if the backend rate-limits or
  // the values are still mid-edit, the explicit "Continue to payment"
  // click in handleGetQuote will surface any real problem with a popup.
  useEffect(() => {
    // Don't run while a quote is already locked, while another request
    // is in flight, before hydration, or while bouncing out.
    if (quote) return undefined;
    if (!pendingSignupId) return undefined;
    if (phase !== 'choosing_plan') return undefined;

    const userCount = Number(watchedUserCount);
    const cycle = watchedBillingCycle;
    if (!Number.isFinite(userCount) || userCount < 1 || userCount > 1000) {
      return undefined;
    }
    if (cycle !== 'monthly' && cycle !== 'annual') return undefined;

    if (livePreviewTimerRef.current) {
      clearTimeout(livePreviewTimerRef.current);
    }
    livePreviewTimerRef.current = setTimeout(async () => {
      setLivePreviewLoading(true);
      try {
        const data = await endpoints.signup.quote({
          pendingSignupId,
          userCount,
          billingCycle: cycle
        });
        setLivePreview(data);
      } catch (_e) {
        // Silent fallback — explicit Continue button reports real errors.
      } finally {
        setLivePreviewLoading(false);
      }
    }, 400);

    return () => {
      if (livePreviewTimerRef.current) {
        clearTimeout(livePreviewTimerRef.current);
        livePreviewTimerRef.current = null;
      }
    };
  }, [watchedUserCount, watchedBillingCycle, pendingSignupId, phase, quote]);

  // ---------------- step 1: get quote (was /signup/quote) ----------------
  async function handleGetQuote(values) {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Another request is already in progress.');
      return;
    }
    inFlightRef.current = true;
    setPhase('fetching_quote');
    try {
      const data = await endpoints.signup.quote({
        pendingSignupId,
        userCount: Number(values.userCount),
        billingCycle: values.billingCycle
      });
      setQuote(data);
      patchSignupState({
        quote: data,
        userCount: Number(values.userCount),
        billingCycle: values.billingCycle
      });
      setPhase('quote_ready');
      popupSuccess(
        'Plan locked',
        `Total ${paiseToRupees(data.totalPaise)}. Enter your master credentials below to create your account.`
      );
    } catch (err) {
      setPhase('choosing_plan');
      popupError('Could not fetch quote', parseApiError(err).description);
    } finally {
      inFlightRef.current = false;
    }
  }

  // ---------------- step 2: pay ----------------
  async function handlePay(values) {
    // Stage 5K-CustomPlan: every public plan now requires Razorpay
    // payment. The legacy no-payment fork (handleActivateCouponPlan)
    // is removed — the backend rejects requires_payment=false at the
    // /signup/quote layer, so this surface never sees a no-payment
    // quote in the first place.
    if (inFlightRef.current) {
      popupWarn(
        'Payment already in progress',
        'Please wait for the current attempt to finish before clicking again.'
      );
      return;
    }

    // Frontend validation gate. AntD's Form.Item rules already enforce these,
    // but we double-check here so a user who somehow bypassed the field-level
    // rule (autofill quirks, paste, etc.) gets a popup instead of a silent
    // backend 400.
    const username = SAFE(values.masterUsername).trim().toLowerCase();
    const masterPassword = SAFE(values.masterPassword);
    if (!isMasterUsername(username)) {
      popupError(
        'Invalid master username',
        'Use 3–30 lowercase letters, digits, dot, dash, or underscore. Must start with a letter.'
      );
      return;
    }
    const strength = passwordStrength(masterPassword);
    if (!strength.ok) {
      popupError(
        'Password is too weak',
        `It needs ${strength.reasons.join(', ')}.`
      );
      return;
    }

    inFlightRef.current = true;
    heldCredentialsRef.current = { username, masterPassword };

    // Re-read store at submit time so prefill/contact never go stale.
    const fresh = getSignupState();

    // 1. Create order if we don't already have a usable one. Always send
    // the same clientAttemptId for the same in-progress attempt so the
    // backend dedupes Razorpay create-order calls across refreshes.
    let liveOrder = order;
    if (!liveOrder || !liveOrder.razorpayOrderId) {
      setPhase('creating_order');
      try {
        liveOrder = await endpoints.signup.createOrder({
          pendingSignupId,
          clientAttemptId
        });
        setOrder(liveOrder);
        patchSignupState({ order: liveOrder });
      } catch (err) {
        setPhase('quote_ready');
        inFlightRef.current = false;
        popupError(
          'Could not create payment order',
          parseApiError(err).description
        );
        return;
      }
    }

    // 2. Open Razorpay (or take the dev mock fallback).
    setPhase('checkout_opened');
    let signaturePayload;
    try {
      if (liveOrder.mode === 'mock') {
        signaturePayload = {
          razorpayPaymentId: 'pay_mock_' + Date.now(),
          razorpayOrderId: liveOrder.razorpayOrderId,
          razorpaySignature: 'mock_signature'
        };
      } else {
        signaturePayload = await openRazorpayCheckout({
          keyId: liveOrder.razorpayKeyId,
          orderId: liveOrder.razorpayOrderId,
          amountPaise: liveOrder.amountPaise,
          currency: liveOrder.currency,
          name: liveOrder.name || 'Glass-i',
          description:
            liveOrder.description || 'Glass-i SaaS subscription',
          prefill: {
            name:
              (liveOrder.prefill && liveOrder.prefill.name) ||
              fresh.contactName,
            email:
              (liveOrder.prefill && liveOrder.prefill.email) ||
              fresh.contactEmail,
            contact:
              (liveOrder.prefill && liveOrder.prefill.contact) ||
              fresh.contactMobile
          },
          notes: { pendingSignupId }
        });
      }
    } catch (checkoutErr) {
      const code = checkoutErr && checkoutErr.code;
      // CRITICAL: never call /signup/verify-payment from a failed checkout.
      // The signature triple was never produced — there is nothing to
      // verify. Razorpay was the source of truth that this attempt did
      // not complete, so we drop the order and rotate the attempt id so
      // the next Pay click is a clean retry, not a reuse.
      if (code === 'CHECKOUT_MISSING_KEY') {
        // Config-only error — no order has been created yet, so we don't
        // need to rotate the attempt id. Stay in quote_ready.
        inFlightRef.current = false;
        setPhase('quote_ready');
        popupError(
          'Razorpay key is missing',
          'Add VITE_RAZORPAY_KEY_ID in frontend .env and restart the frontend dev server.'
        );
      } else if (code === 'CHECKOUT_DISMISSED') {
        resetPaymentForFreshAttempt({ nextPhase: 'payment_cancelled' });
        popupInfo(
          'Payment was cancelled',
          'You can try again when ready. No charge was made.'
        );
      } else if (code === 'CHECKOUT_FAILED') {
        resetPaymentForFreshAttempt({ nextPhase: 'payment_failed' });
        popupError(
          'Payment failed',
          'Your payment was not completed. ' +
            describeCheckoutError(checkoutErr) +
            ' Please try again with a fresh payment attempt.'
        );
      } else if (code === 'CHECKOUT_LOAD_FAILED') {
        resetPaymentForFreshAttempt({ nextPhase: 'payment_failed' });
        popupError(
          'Razorpay could not be loaded',
          describeCheckoutError(checkoutErr)
        );
      } else if (
        code === 'CHECKOUT_BAD_ORDER_ID' ||
        code === 'CHECKOUT_BAD_AMOUNT' ||
        code === 'CHECKOUT_BAD_CURRENCY'
      ) {
        // The order from the backend was malformed or stale. Clear it +
        // rotate the attempt id so the next Pay click builds a fresh
        // order; stay in quote_ready so the form is interactive.
        resetPaymentForFreshAttempt({ nextPhase: 'quote_ready' });
        popupError(
          'Order details look invalid',
          describeCheckoutError(checkoutErr) +
            ' We cleared the previous order — click Pay again to retry.'
        );
      } else {
        resetPaymentForFreshAttempt({ nextPhase: 'payment_failed' });
        popupError(
          'Payment could not be completed',
          describeCheckoutError(checkoutErr)
        );
      }
      return;
    }

    // 3. We have the signed triple. Hold it for retry, run server verify.
    setLastSignature(signaturePayload);
    notification.info({
      message: 'Payment received. Verifying securely…',
      description:
        'Confirming with Razorpay and setting up your account. This can take up to ~20 seconds.',
      placement: 'topRight',
      duration: 4
    });
    await runVerification(signaturePayload, username, masterPassword);
  }

  // ---------------- step 3: verify (own function so retry can re-enter) ----
  async function runVerification(signaturePayload, username, masterPassword) {
    setPhase('verification_pending');
    try {
      const result = await endpoints.signup.verifyPayment({
        pendingSignupId,
        razorpayOrderId: signaturePayload.razorpayOrderId,
        razorpayPaymentId: signaturePayload.razorpayPaymentId,
        razorpaySignature: signaturePayload.razorpaySignature,
        masterUsername: username,
        masterPassword
      });
      patchSignupState({
        provisioned: result,
        masterUsername: username
      });
      heldCredentialsRef.current = null;
      setPhase('verified');
      popupSuccess(
        'Payment verified successfully',
        'Setting up your account…'
      );
      // Brief delay so the success popup is visible before unmount.
      setTimeout(() => {
        setPhase('completed');
        navigate('/signup/done', { replace: true });
      }, 350);
    } catch (err) {
      setPhase('verification_failed');
      const status = err && err.response && err.response.status;
      const code =
        err &&
        err.response &&
        err.response.data &&
        err.response.data.error &&
        err.response.data.error.code;
      // Safe diagnostic — code/status only, no signatures or passwords.
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[signup-pay] verify-payment failed', { status, code });
      }
      popupError(
        'Payment could not be verified',
        parseApiError(err).description +
          ' If the amount was deducted, please contact support with your payment reference. ' +
          'You can also retry verification — we will not charge you twice.'
      );
    } finally {
      inFlightRef.current = false;
    }
  }

  async function handleRetryVerification() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Verification is already in progress.');
      return;
    }
    if (!lastSignature || !heldCredentialsRef.current) {
      popupWarn(
        'Cannot retry verification',
        'No payment reference is available. Please start the payment again.'
      );
      return;
    }
    inFlightRef.current = true;
    const { username, masterPassword } = heldCredentialsRef.current;
    await runVerification(lastSignature, username, masterPassword);
  }

  function handleBackToPlans() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Cannot go back while a request is in progress.');
      return;
    }
    setQuote(null);
    setOrder(null);
    setLastSignature(null);
    setResumedAttempt(false);
    heldCredentialsRef.current = null;
    // Stage 5K-4B: drop back to the plan-picker view so the user
    // re-chooses Trial vs Standard (rather than landing on the
    // Standard configure surface).
    setPlanView('cards');
    // Mint a fresh attempt id — the user is starting over, so the new
    // create-order should produce a new Razorpay order rather than reusing
    // anything from the previous attempt.
    const fresh = generateClientAttemptId();
    setClientAttemptId(fresh);
    patchSignupState({
      quote: null,
      order: null,
      provisioned: null,
      clientAttemptId: fresh
    });
    setPhase('choosing_plan');
    planForm.resetFields();
  }

  // Stage 5K-4A polish: "Continue to payment" lives in the Order
  // Summary section now (not inside the Standard Plan card), so it
  // calls planForm.validateFields() directly to grab the latest
  // user count + billing cycle, then runs the existing handleGetQuote
  // that locks the quote and transitions phase to 'quote_ready'.
  // Razorpay path / verify-payment / recover-payment are unaffected.
  async function handleProceedToPayment() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Another request is already in progress.');
      return;
    }
    let values;
    try {
      values = await planForm.validateFields();
    } catch (_e) {
      popupWarn(
        'Please complete the plan details',
        'User count and billing cycle are required before continuing.'
      );
      return;
    }
    await handleGetQuote(values);
  }

  // Drop everything tied to a single payment attempt (stale order ref,
  // last signature, held credentials) and mint a fresh client_attempt_id
  // so the next create-order call produces a brand-new Razorpay order
  // rather than reusing the failed/cancelled one. The quote and master
  // username are intentionally retained so the user doesn't re-pick a
  // plan or retype the username; the master password field is wiped so
  // the user revalidates it for the new attempt. Used by Razorpay's
  // payment.failed / dismiss handlers AND the recovery-banner buttons.
  function resetPaymentForFreshAttempt({ nextPhase = 'quote_ready' } = {}) {
    setOrder(null);
    setLastSignature(null);
    heldCredentialsRef.current = null;
    setResumedAttempt(false);
    const freshAid = generateClientAttemptId();
    setClientAttemptId(freshAid);
    patchSignupState({ order: null, clientAttemptId: freshAid });
    // Force re-entry of the master password — same-tab users routinely
    // typed it for the previous attempt and we don't keep that in
    // sessionStorage, but AntD's <Input.Password> still holds the value
    // in the form instance. Wipe it so the next Pay click revalidates.
    try {
      credForm.setFieldValue('masterPassword', '');
    } catch (_e) {
      /* form instance not yet bound; safe to ignore */
    }
    inFlightRef.current = false;
    setPhase(nextPhase);
  }

  // ---------------- Stage 5K-4B: paid trial handlers ----------------
  // Public onboarding now offers a paid ₹99 trial (TRIAL_7D_99) instead
  // of the no-payment activation flow. The Trial card re-uses the
  // existing paid Razorpay chain — fetch quote with planCode → lock
  // quote → existing credentials surface → Razorpay → verify-payment.
  // We do NOT call /signup/activate-free-trial from public UI.
  async function handleStartTrial() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Another request is already in progress.');
      return;
    }
    inFlightRef.current = true;
    setPhase('fetching_quote');
    try {
      const data = await endpoints.signup.quote({
        pendingSignupId,
        planCode: TRIAL_PLAN.planCode
      });
      setQuote(data);
      // Persist only safe trial metadata; the master password is never
      // stored. The quote response itself carries planCode/planName/
      // trialDays/includedUsers from the backend, which is what the
      // Selected-plan strip + done page read.
      patchSignupState({
        quote: data,
        userCount: data.includedUsers,
        billingCycle: 'trial'
      });
      setPlanView('cards');
      setPhase('quote_ready');
      popupSuccess(
        'Trial Plan selected',
        `Total ${paiseToRupees(data.totalPaise)} including GST. Enter your master credentials below to continue to payment.`
      );
    } catch (err) {
      setPhase('choosing_plan');
      const status = err && err.response && err.response.status;
      const code =
        err &&
        err.response &&
        err.response.data &&
        err.response.data.error &&
        err.response.data.error.code;
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[signup-trial] quote failed', { status, code });
      }
      // Stage 5N-13: graceful handling for the two specific 409s a
      // /signup/quote-with-trial-plan-code can return. Disable the
      // Trial card up-front so the user is guided to Standard Plan
      // instead of being shown a scary popup over and over.
      if (code === 'TRIAL_ALREADY_USED') {
        setTrialEligibility({ eligible: false, reason: 'trial_already_used' });
        popupWarn(
          'Trial already used',
          'Trial already used for this GSTIN. Please choose the Standard Plan.'
        );
        return;
      }
      if (code === 'SEZ_APPROVAL_PENDING') {
        setTrialEligibility({ eligible: false, reason: 'sez_approval_pending' });
        popupWarn(
          'SEZ approval pending',
          'Your SEZ registration is pending internal approval. Payment will be enabled after approval.'
        );
        return;
      }
      if (code === 'SEZ_APPROVAL_REJECTED') {
        setTrialEligibility({ eligible: false, reason: 'sez_approval_rejected' });
        popupError(
          'SEZ registration rejected',
          'Your SEZ registration was rejected. Please contact support.'
        );
        return;
      }
      popupError(
        'Could not start the trial',
        parseApiError(err).description
      );
    } finally {
      inFlightRef.current = false;
    }
  }

  // Stage 5K-CustomPlan: apply a Custom Plan code. Calls /signup/quote
  // with the customCode field; the backend re-validates the code
  // against the pending signup's gstin/email and computes the quote
  // server-side from the linked custom plan. requires_payment=false
  // codes are rejected at the /signup/quote layer with
  // CUSTOM_PLAN_PAYMENT_REQUIRED, so this handler always lands on a
  // paid quote that flows into the existing Razorpay path.
  async function handleApplyCustomCode() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Another request is already in progress.');
      return;
    }
    const trimmed = String(customCodeInput || '').trim().toUpperCase();
    if (!/^[A-Z0-9_-]{3,40}$/.test(trimmed)) {
      popupError(
        'Invalid custom code',
        'Custom code must be 3–40 characters using A–Z, 0–9, dash, or underscore.'
      );
      return;
    }
    inFlightRef.current = true;
    setCustomCodeBusy(true);
    setPhase('fetching_quote');
    try {
      const data = await endpoints.signup.quote({
        pendingSignupId,
        customCode: trimmed
      });
      // Defense-in-depth: backend already rejects requires_payment=false,
      // but if a misconfigured response ever slipped through we refuse
      // to render a no-payment custom plan UI.
      if (data && data.requiresPayment === false) {
        setPhase('choosing_plan');
        popupError(
          'Custom plan unavailable',
          'This custom plan is not available for public signup. Please contact support.'
        );
        return;
      }
      setQuote(data);
      patchSignupState({
        quote: data,
        userCount: data.includedUsers,
        billingCycle: data.billingCycle || 'custom'
      });
      // Drop back to the cards view so the applied custom plan card +
      // credentials surface render side-by-side with the other plans.
      setPlanView('cards');
      setPhase('quote_ready');
      popupSuccess(
        'Custom plan applied',
        `${data.planName} — Total ${paiseToRupees(data.totalPaise)} including GST. Enter your master credentials below to continue to payment.`
      );
    } catch (err) {
      setPhase('choosing_plan');
      popupError(
        'Could not apply custom code',
        parseApiError(err).description
      );
    } finally {
      setCustomCodeBusy(false);
      inFlightRef.current = false;
    }
  }

  // Stage 5K-4B: switch the plan picker into the Standard configure
  // sub-view. No network call — the inputs and order summary are
  // populated by the existing live-preview /signup/quote effect.
  function handleSelectStandard() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Another request is already in progress.');
      return;
    }
    setPlanView('configure_standard');
  }

  // Stage 5K-CustomPlan: switch into the Custom Plan code-entry sub-view.
  function handleSelectCustomPlan() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Another request is already in progress.');
      return;
    }
    setCustomCodeInput('');
    setPlanView('custom_code');
  }

  function handleBackFromConfigure() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Cannot go back while a request is in progress.');
      return;
    }
    setPlanView('cards');
  }

  // Stage 5J-D: post-Razorpay-capture recovery wired to the recovery
  // banner. Called when the user clicks "I already paid — recover" and
  // we have a known order id (from sessionStorage) but the client lost the
  // signature triple (refresh / browser close). Validates the credentials
  // form, posts to /signup/recover-payment, and lets the backend re-fetch
  // from Razorpay to confirm the payment is real.
  async function handleRecoverPaidAttempt() {
    if (inFlightRef.current) {
      popupWarn('Please wait', 'Another request is already in progress.');
      return;
    }
    if (!order || !order.razorpayOrderId) {
      popupWarn(
        'No order reference',
        'There is no Razorpay order id to recover from. Click Pay to start a fresh payment.'
      );
      return;
    }

    let creds;
    try {
      creds = await credForm.validateFields(['masterUsername', 'masterPassword']);
    } catch (_e) {
      // AntD Form.Item rules surfaced field-level errors. Add a popup so
      // the user understands why nothing happened.
      popupWarn(
        'Enter master credentials first',
        'Please fill in master username and password before recovering.'
      );
      return;
    }

    const username = SAFE(creds.masterUsername).trim().toLowerCase();
    const masterPassword = SAFE(creds.masterPassword);

    inFlightRef.current = true;
    setPhase('verification_pending');
    notification.info({
      message: 'Recovering payment…',
      description:
        'Confirming with Razorpay and setting up your account. This can take up to ~20 seconds.',
      placement: 'topRight',
      duration: 4
    });

    try {
      const result = await endpoints.signup.recoverPayment({
        pendingSignupId,
        razorpayOrderId: order.razorpayOrderId,
        masterUsername: username,
        masterPassword
      });

      // Recovery returns either a provisioning result OR a structured
      // not_paid envelope. Distinguish on the presence of tenantId.
      if (result && result.tenantId) {
        patchSignupState({
          provisioned: result,
          masterUsername: username
        });
        heldCredentialsRef.current = null;
        setPhase('verified');
        notification.success({
          message: 'Payment verified successfully',
          description: 'Setting up your account…',
          placement: 'topRight',
          duration: 4
        });
        setTimeout(() => {
          setPhase('completed');
          navigate('/signup/done', { replace: true });
        }, 350);
      } else {
        // Backend says no captured payment was found at Razorpay.
        setPhase('quote_ready');
        popupError(
          'No completed payment was found for this attempt',
          (result && result.message
            ? result.message + ' '
            : '') +
            'Please start a fresh payment.'
        );
      }
    } catch (err) {
      setPhase('verification_failed');
      const status = err && err.response && err.response.status;
      const code =
        err &&
        err.response &&
        err.response.data &&
        err.response.data.error &&
        err.response.data.error.code;
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[signup-recover] failed', { status, code });
      }
      popupError(
        'Could not recover the payment',
        parseApiError(err).description +
          ' If the amount was deducted, please contact support with your payment reference.'
      );
    } finally {
      inFlightRef.current = false;
    }
  }

  // ---------------- derived UI flags ----------------
  const isActive = ACTIVE_PHASES.has(phase);
  const showVerifyOverlay =
    phase === 'verification_pending' || phase === 'verification_slow';
  const showRetryBlock = phase === 'verification_failed' && !!lastSignature;
  // Stage 5K-4B: gate for the plan-picker surface (cards or configure).
  // Disappears once a quote is locked (paid standard or paid trial) —
  // the credentials + Pay surface takes over from there.
  const isPlanStep =
    !quote && (phase === 'choosing_plan' || phase === 'fetching_quote');
  // True when the locked quote is a paid trial (TRIAL_7D_99). Drives
  // the wording on the Selected-plan strip, the Pay button label, and
  // the success popup so the trial / standard surfaces speak the
  // user's language without forking the form.
  const isTrialQuote =
    quote && quote.planCode === TRIAL_PLAN.planCode;

  // Surfaced when AntD's Form-level validation fires onFinishFailed
  // (htmlType="submit" path). We don't gate the Pay button on a password
  // watcher anymore — browser autofill can populate the field before
  // Form.useWatch picks it up, leaving the button visually disabled even
  // when the value is actually present. Letting the user click and then
  // popping a clear validation summary is more reliable.
  function handlePayValidationFailed() {
    popupWarn(
      'Please correct the highlighted fields',
      'Master username and password are required, and the password must meet the strength rules.'
    );
  }

  // ---------------- render ----------------
  if (phase === 'initializing' || phase === 'session_expired') {
    // Don't render the full surface before hydration / while bouncing out.
    return (
      <div className="flex items-center justify-center min-h-[160px]">
        <Spin
          indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />}
          tip="Restoring signup…"
        >
          <div className="min-w-[80px] min-h-[40px]" />
        </Spin>
      </div>
    );
  }

  // Header copy depends on the phase: choosing a plan, configuring a
  // standard plan, or completing the credentials/payment surface.
  // The plan-step header keeps the user oriented while the payment-
  // step header switches to the "Complete your account setup" copy
  // shown in the screenshot.
  const headerTitle = quote
    ? 'Complete your account setup'
    : 'Choose your plan';
  const headerSubtitle = (() => {
    if (order && order.mode === 'mock') {
      return (
        <>
          Backend is in <code className="text-xs">RAZORPAY_ENV=mock</code>{' '}
          mode — we post the same mock signature shape Newman uses, no
          Razorpay JS widget is loaded.
        </>
      );
    }
    if (quote) {
      return (
        <>
          Review your plan, create your master login, and finish payment
          securely.
        </>
      );
    }
    return (
      <>
        Start with a trial or choose a paid plan. You can change plans
        later.
      </>
    );
  })();

  return (
    <div>
      <div className="mt-2 mb-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0 leading-tight">
          {headerTitle}
        </h2>
        <p className="text-sm sm:text-base text-ink-500 mt-1.5 mb-0 max-w-2xl">
          {headerSubtitle}
        </p>
      </div>

      {/* Verifying overlay (in-page) — popups are still fired in parallel. */}
      {showVerifyOverlay ? (
        <div className="mb-5 rounded-xl border border-brand-200 bg-brand-50 px-4 py-4 flex items-center gap-3">
          <Spin
            indicator={
              <LoadingOutlined
                spin
                className="text-brand-500"
                style={{ fontSize: 22 }}
              />
            }
          />
          <div className="min-w-0">
            <p className="font-semibold m-0 text-brand-700">
              {phase === 'verification_slow'
                ? 'Verification is taking longer than expected…'
                : 'Verifying payment…'}
            </p>
            <p className="text-sm m-0 text-brand-700/80">
              {phase === 'verification_slow'
                ? 'Please do not pay again. We are still confirming with Razorpay and setting up your account. You can retry verification below if it fails.'
                : 'Confirming with Razorpay, setting up your account, and generating your invoice.'}
            </p>
          </div>
        </div>
      ) : null}


      {/* Failure / cancellation / recovery banner. Mutually exclusive
          variants so the user is never offered "Continue" on top of a
          known-failed attempt. Order matters: failed/cancelled phases
          take precedence over the resumed-attempt hint. */}
      {phase === 'payment_failed' && quote ? (
        <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-4">
          <div className="flex items-start gap-3">
            <CloseCircleOutlined className="text-rose-600 text-lg mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold m-0 text-rose-800">
                Payment attempt failed
              </p>
              <p className="text-sm m-0 mt-0.5 text-rose-700">
                Your previous payment attempt was not completed. Please start
                a fresh payment — re-enter your master password below and
                click Pay to create a brand-new order.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() =>
                resetPaymentForFreshAttempt({ nextPhase: 'quote_ready' })
              }
              disabled={isActive}
            >
              Try again with fresh payment
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToPlans}
              disabled={isActive}
            >
              Change plan
            </Button>
          </div>
        </div>
      ) : phase === 'payment_cancelled' && quote ? (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
          <div className="flex items-start gap-3">
            <InfoCircleOutlined className="text-amber-600 text-lg mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold m-0 text-amber-800">
                Payment was cancelled
              </p>
              <p className="text-sm m-0 mt-0.5 text-amber-700">
                You closed the Razorpay window before paying. No charge was
                made. Re-enter your master password and click Pay to start a
                fresh attempt.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() =>
                resetPaymentForFreshAttempt({ nextPhase: 'quote_ready' })
              }
              disabled={isActive}
            >
              Try again with fresh payment
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToPlans}
              disabled={isActive}
            >
              Change plan
            </Button>
          </div>
        </div>
      ) : resumedAttempt && quote ? (
        <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-4">
          <div className="flex items-start gap-3">
            <InfoCircleOutlined className="text-blue-600 text-lg mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold m-0 text-blue-800">
                You have an unfinished payment attempt
              </p>
              <p className="text-sm m-0 mt-0.5 text-blue-700">
                We restored your plan{order ? ' and pending order reference' : ''} from this
                browser session. Choose how to proceed — your master password
                was not stored and must be re-entered below.
              </p>
              {order && order.razorpayOrderId ? (
                <p className="text-xs mt-2 mb-0 text-blue-700">
                  Pending order ref:{' '}
                  <code className="text-xs">{order.razorpayOrderId}</code>
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              type="primary"
              icon={<CreditCardOutlined />}
              onClick={() => setResumedAttempt(false)}
              disabled={isActive}
            >
              Continue payment
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRecoverPaidAttempt}
              disabled={isActive}
            >
              I already paid — recover
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                resetPaymentForFreshAttempt({ nextPhase: 'quote_ready' });
                notification.info({
                  message: 'Starting fresh payment',
                  description:
                    'The previous order was cleared. Enter your master password and click Pay to create a new order.',
                  placement: 'topRight',
                  duration: 4
                });
              }}
              disabled={isActive}
            >
              Start fresh payment
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToPlans}
              disabled={isActive}
            >
              Change plan
            </Button>
          </div>
        </div>
      ) : null}

      {/* Retry block (after a verify-payment failure with a known triple). */}
      {showRetryBlock ? (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
          <div className="flex items-start gap-3">
            <InfoCircleOutlined className="text-amber-600 text-lg mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold m-0 text-amber-800">
                Verification failed — but your payment may already be captured at Razorpay
              </p>
              <p className="text-sm m-0 mt-0.5 text-amber-700">
                You can retry verification with the same payment reference.
                We will not charge you again. If the issue continues, contact
                support with the reference shown below.
              </p>
              <p className="text-xs mt-2 mb-0 text-amber-700">
                Order ref:{' '}
                <code className="text-xs">{lastSignature.razorpayOrderId}</code>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRetryVerification}
            >
              Retry verification
            </Button>
            <Button onClick={handleBackToPlans} icon={<ArrowLeftOutlined />}>
              Back to plans
            </Button>
          </div>
        </div>
      ) : null}

      {/* PLAN SELECTION.
          Stage 5K-4B: simple two-card picker by default. Trial card
          fetches a paid ₹99 quote and routes directly to the existing
          credentials/Razorpay surface. Standard card opens a configure
          sub-view (user count + billing cycle + order summary) that
          ends in the same credentials/Razorpay surface. Both flows
          share a single locked `quote` -> handleGetQuote -> Razorpay
          chain — only the plan-picker JSX differs. */}
      {isPlanStep && planView === 'cards' ? (
        <div className="space-y-5 sm:space-y-6 mb-4">
          {/* ---- Trial Plan ---- */}
          {/* Stage 5N-13: when /signup/status reports trial-already-used
              (or an SEZ gate fires first) we render the card visually
              de-emphasised with a clear note instead of letting the
              user click and surfacing a 409. The Standard Plan card
              below stays fully active. */}
          <div
            className={
              trialEligibility.eligible
                ? 'rounded-2xl border border-surface-200 bg-white p-3.5 sm:p-4 shadow-card hover:border-emerald-200 hover:shadow-elevated transition-colors'
                : 'rounded-2xl border border-surface-200 bg-surface-50 p-3.5 sm:p-4 opacity-70'
            }
          >
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 shrink-0"
                aria-hidden="true"
              >
                <LeafIcon className="h-5 w-5 text-emerald-600" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
                    Trial Plan
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-ink-900 tabular-nums">
                    {TRIAL_PLAN.displayPrice}
                  </span>
                  <span className="text-xs text-ink-500">
                    {TRIAL_PLAN.displayPriceNote}
                  </span>
                </div>
                {trialEligibility.eligible ? (
                  <p className="text-xs sm:text-sm text-ink-600 m-0 mt-1 truncate">
                    7 days access · 5 users · Secure payment required
                  </p>
                ) : trialEligibility.reason === 'sez_approval_pending' ? (
                  <p className="text-xs sm:text-sm text-amber-700 m-0 mt-1">
                    SEZ registration is pending internal approval. Payment
                    will be enabled after approval.
                  </p>
                ) : trialEligibility.reason === 'sez_approval_rejected' ? (
                  <p className="text-xs sm:text-sm text-rose-700 m-0 mt-1">
                    SEZ registration was rejected. Please contact support.
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm text-ink-600 m-0 mt-1">
                    Trial already used for this GSTIN. Please choose the
                    Standard Plan.
                  </p>
                )}
              </div>
              <Button
                type="primary"
                onClick={handleStartTrial}
                loading={phase === 'fetching_quote'}
                disabled={isActive || !trialEligibility.eligible}
                className="!bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 shrink-0 !font-semibold"
              >
                {trialEligibility.eligible
                  ? 'Start trial'
                  : trialEligibility.reason === 'trial_already_used'
                    ? 'Trial used'
                    : 'Trial unavailable'}
              </Button>
            </div>
          </div>

          {/* ---- Standard Plan (Most popular) ---- */}
          <div className="relative rounded-2xl border-2 border-brand-500 bg-white p-3.5 sm:p-4 shadow-elevated">
            <span
              className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-brand-500 text-white text-[10px] font-semibold px-2 py-0.5 shadow-[0_4px_8px_-2px_rgba(24,119,242,0.55)]"
              aria-hidden="true"
            >
              <StarFilled className="text-amber-300 text-[10px]" />
              Most popular
            </span>
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 shrink-0"
                aria-hidden="true"
              >
                <CrownOutlined className="text-brand-500 text-lg" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-700">
                    Standard Plan
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-ink-900 tabular-nums">
                    {pricingConfig
                      ? `₹${(pricingConfig.pricePerUserPaise / 100).toLocaleString('en-IN')}`
                      : '₹1,999'}
                  </span>
                  <span className="text-xs text-ink-500">
                    /user / month + {pricingConfig ? `${pricingConfig.gstPercent}% ` : ''}GST
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-ink-600 m-0 mt-1 truncate">
                  Monthly or annual · Add users as needed
                  {' · '}
                  {pricingConfig && pricingConfig.annualBenefitType === 'percent_discount'
                    ? `${pricingConfig.annualDiscountPercent}% annual discount`
                    : pricingConfig && pricingConfig.annualBenefitType === 'fixed_discount'
                      ? `₹${(pricingConfig.annualDiscountPaise / 100).toLocaleString('en-IN')} annual discount`
                      : pricingConfig && pricingConfig.annualBenefitType === 'free_months'
                        ? `${pricingConfig.annualFreeMonths} free months on annual`
                        : 'Annual discount'}
                </p>
              </div>
              <Button
                type="primary"
                onClick={handleSelectStandard}
                disabled={isActive}
                className="shrink-0 !font-semibold"
              >
                Configure
              </Button>
            </div>
          </div>

          {/* ---- Custom Plan ---- */}
          <div className="rounded-2xl border border-surface-200 bg-white p-3.5 sm:p-4 shadow-card hover:border-violet-200 hover:shadow-elevated transition-colors">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-50 shrink-0"
                aria-hidden="true"
              >
                <ApartmentOutlined className="text-violet-600 text-lg" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-700">
                    Custom Plan
                  </span>
                  <span className="text-base font-bold text-ink-900">
                    Enterprise / custom offers
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-ink-600 m-0 mt-1 truncate">
                  Tailored users and duration · Use the code shared by glass-i
                </p>
              </div>
              <Button
                type="primary"
                onClick={handleSelectCustomPlan}
                disabled={isActive}
                className="!bg-violet-600 hover:!bg-violet-700 !border-violet-600 shrink-0 !font-semibold"
              >
                Enter code
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Stage 5K-CustomPlan: Custom Plan code-entry sub-view. Single
          input + Apply + Back to plans. Backend remains the source of
          truth for the resolved plan name / amount / duration. */}
      {isPlanStep && planView === 'custom_code' ? (
        <div className="rounded-2xl border border-surface-200 bg-white overflow-hidden mb-4">
          <div className="px-4 sm:px-5 py-3 border-b border-surface-200 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-600"
                aria-hidden="true"
              >
                <ApartmentOutlined />
              </span>
              <p className="text-base font-semibold text-ink-900 m-0 truncate">
                Enter custom code
              </p>
            </div>
            <Button
              type="link"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={handleBackFromConfigure}
              disabled={isActive}
              className="!px-0 !font-semibold"
            >
              Back to plans
            </Button>
          </div>
          <div className="p-4 sm:p-5">
            <p className="text-sm text-ink-600 m-0 mb-4">
              Enter the custom code shared by glass-i to view your custom
              plan and payable amount.
            </p>
            <div className="flex flex-wrap items-stretch gap-2.5">
              <Input
                value={customCodeInput}
                onChange={(e) =>
                  setCustomCodeInput(
                    String(e.target.value || '')
                      .replace(/[^A-Za-z0-9_-]+/g, '')
                      .toUpperCase()
                      .slice(0, 40)
                  )
                }
                placeholder="ENTERPRISE99"
                size="large"
                maxLength={40}
                disabled={isActive || customCodeBusy}
                className="!flex-1 !min-w-[220px] font-mono uppercase tracking-wider"
                onPressEnter={handleApplyCustomCode}
              />
              <Button
                type="primary"
                size="large"
                onClick={handleApplyCustomCode}
                loading={customCodeBusy}
                disabled={isActive || !customCodeInput}
                className="!h-12 !px-5 !text-base !font-semibold !bg-violet-600 hover:!bg-violet-700 !border-violet-600"
              >
                Apply code
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Stage 5K-CustomPlan: detailed "Custom plan applied" summary.
          Renders ABOVE the credentials form whenever a Custom Plan
          quote is locked, replacing the generic Selected-plan strip
          for that case. Shows a full Base/GST/Total breakdown so the
          user knows exactly what they're about to pay. The credentials
          form below is the implicit "Continue to payment" surface —
          its Pay button label already says "Pay {total} & create
          account". */}
      {quote && (quote.customPlanApplied || quote.couponApplied) ? (
        <div className="rounded-2xl border border-violet-200 bg-white overflow-hidden mb-4 shadow-card">
          <div className="px-4 sm:px-5 py-3 border-b border-violet-100 flex flex-wrap items-center justify-between gap-3 bg-violet-50/40">
            <div className="flex items-center gap-2.5 min-w-0">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-600"
                aria-hidden="true"
              >
                <ApartmentOutlined />
              </span>
              <p className="text-base font-semibold text-violet-700 m-0 truncate">
                Custom plan applied
              </p>
            </div>
            <Button
              type="link"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToPlans}
              disabled={isActive}
              className="!px-0 !font-semibold !text-violet-600 hover:!text-violet-700"
            >
              Change plan
            </Button>
          </div>
          <div className="divide-y divide-surface-200 px-4 sm:px-5">
            <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
              <span className="text-ink-600">Plan</span>
              <span className="font-semibold text-ink-900 text-right truncate">
                {quote.planName}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
              <span className="text-ink-600">Custom code</span>
              <code className="text-xs font-semibold text-violet-700 bg-violet-50 px-2 py-0.5 rounded-md">
                {quote.customCode || quote.couponCode}
              </code>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
              <span className="text-ink-600">Included users</span>
              <span className="font-semibold text-ink-900 tabular-nums">
                {quote.includedUsers} user{quote.includedUsers === 1 ? '' : 's'}
              </span>
            </div>
            {quote.durationDays ? (
              <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
                <span className="text-ink-600">Duration</span>
                <span className="font-semibold text-ink-900 tabular-nums">
                  {quote.durationDays} days
                </span>
              </div>
            ) : quote.trialDays ? (
              <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
                <span className="text-ink-600">Trial</span>
                <span className="font-semibold text-ink-900 tabular-nums">
                  {quote.trialDays} days
                </span>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
              <span className="text-ink-600">Base</span>
              <span className="font-semibold text-ink-900 tabular-nums whitespace-nowrap">
                {paiseToRupees(quote.basePaise)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
              <span className="text-ink-600">GST</span>
              <span className="font-semibold text-ink-900 tabular-nums whitespace-nowrap">
                {paiseToRupees(quote.gstPaise)}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 px-4 sm:px-5 py-3 bg-violet-50 border-t border-violet-100">
            <span className="text-base font-bold text-ink-900 whitespace-nowrap">
              Total payable
            </span>
            <span className="text-2xl font-bold text-violet-700 text-right whitespace-nowrap tabular-nums">
              {paiseToRupees(quote.totalPaise)}
            </span>
          </div>
        </div>
      ) : null}

      {isPlanStep && planView === 'configure_standard' ? (
        <Form
          form={planForm}
          layout="vertical"
          initialValues={{
            userCount: getSignupState().userCount || 5,
            billingCycle: getSignupState().billingCycle || 'annual'
          }}
          onFinish={handleGetQuote}
          disabled={isActive}
          size="large"
          className="onboarding-compact-form"
        >
          {/* Configure Standard Plan card */}
          <div className="rounded-2xl border border-surface-200 bg-white overflow-hidden mb-3">
            <div className="px-4 py-2.5 border-b border-surface-200 flex items-center justify-between gap-3 min-w-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-500 shrink-0"
                  aria-hidden="true"
                >
                  <SettingOutlined />
                </span>
                <p className="text-sm sm:text-base font-semibold text-ink-900 m-0 truncate">
                  Configure Standard Plan
                </p>
              </div>
              <Button
                type="link"
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackFromConfigure}
                disabled={isActive}
                className="!px-0 !font-semibold shrink-0"
              >
                Back to plans
              </Button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-1 mb-1">
                <Form.Item
                  label={
                    <span className="text-sm font-semibold text-ink-800">
                      <span className="text-rose-500 mr-1">*</span>
                      Number of users
                    </span>
                  }
                  name="userCount"
                  rules={[{ required: true, message: 'user count is required' }]}
                  extra={
                    <span className="text-xs text-ink-500">
                      You can add more users later.
                    </span>
                  }
                >
                  <InputNumber
                    min={1}
                    max={1000}
                    prefix={<TeamOutlined className="text-ink-400" />}
                    className="w-full"
                  />
                </Form.Item>
                <Form.Item
                  label={
                    <span className="text-sm font-semibold text-ink-800">
                      <span className="text-rose-500 mr-1">*</span>
                      Billing cycle
                    </span>
                  }
                  name="billingCycle"
                  rules={[{ required: true, message: 'billing cycle is required' }]}
                >
                  <Segmented
                    block
                    size="large"
                    className="billing-cycle-segmented"
                    options={[
                      {
                        label: (
                          <span className="inline-flex items-center gap-1.5">
                            <FileTextOutlined />
                            Monthly
                          </span>
                        ),
                        value: 'monthly'
                      },
                      {
                        label: (
                          <span className="inline-flex items-center gap-1.5">
                            <FileTextOutlined />
                            Annual
                          </span>
                        ),
                        value: 'annual'
                      }
                    ]}
                  />
                </Form.Item>
              </div>
            </div>
          </div>

          {/* Order summary card */}
          <div className="rounded-2xl border border-surface-200 bg-white overflow-hidden mb-3">
            <div className="px-4 py-2.5 border-b border-surface-200 flex items-center justify-between gap-3 min-w-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-500 shrink-0"
                  aria-hidden="true"
                >
                  <FileTextOutlined />
                </span>
                <p className="text-sm sm:text-base font-semibold text-ink-900 m-0 truncate">
                  Order summary
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-brand-600 bg-brand-50 border border-brand-100 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0">
                {livePreviewLoading ? (
                  <>
                    <LoadingOutlined spin /> Updating…
                  </>
                ) : (
                  <>
                    <EyeOutlined /> Live preview
                  </>
                )}
              </span>
            </div>
            {livePreview ? (
              <>
                <div className="divide-y divide-surface-200 px-4">
                  <div className="flex items-center justify-between gap-3 py-2 text-sm min-w-0">
                    <span className="text-ink-600">Users</span>
                    <span className="font-semibold text-ink-900 text-right whitespace-nowrap tabular-nums">
                      {Number(watchedUserCount) || ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-2 text-sm min-w-0">
                    <span className="text-ink-600">Billing cycle</span>
                    <Tag color="blue" className="!m-0 capitalize !font-medium">
                      {watchedBillingCycle}
                    </Tag>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-2 text-sm min-w-0">
                    <span className="text-ink-600">Base</span>
                    <span className="font-semibold text-ink-900 text-right whitespace-nowrap tabular-nums">
                      {paiseToRupees(livePreview.basePaise)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-2 text-sm min-w-0">
                    <span className="text-ink-600">Discount</span>
                    <span className="font-semibold text-emerald-600 text-right whitespace-nowrap tabular-nums">
                      − {paiseToRupees(livePreview.discountPaise)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 py-2 text-sm min-w-0">
                    <span className="text-ink-600">GST</span>
                    <span className="font-semibold text-ink-900 text-right whitespace-nowrap tabular-nums">
                      {paiseToRupees(livePreview.gstPaise)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-brand-50 border-t border-surface-200 min-w-0">
                  <span className="text-sm sm:text-base font-bold text-ink-900 whitespace-nowrap">
                    Total payable
                  </span>
                  <span className="text-xl sm:text-2xl font-bold text-brand-700 text-right whitespace-nowrap tabular-nums">
                    {paiseToRupees(livePreview.totalPaise)}
                  </span>
                </div>
              </>
            ) : (
              <div className="px-4 py-5 flex items-center justify-center gap-3 text-sm text-ink-500">
                <LoadingOutlined spin />
                Calculating order summary…
              </div>
            )}
          </div>

          <Form.Item className="!mb-0">
            <Button
              type="primary"
              htmlType="submit"
              loading={phase === 'fetching_quote'}
              icon={<CreditCardOutlined />}
              block
              className="!h-12 !text-base !font-semibold"
            >
              Continue to payment
            </Button>
          </Form.Item>
        </Form>
      ) : null}

      {/* Stage 5K-4B: free-trial credentials sub-step removed. Public
          onboarding now uses the paid ₹99 trial which routes through
          the existing paid credentials + Razorpay surface below. The
          old /signup/activate-free-trial endpoint is preserved for
          internal/dev use but no longer triggered from this page. */}

      {/* SELECTED-PLAN SUMMARY CARD — replaces the compact strip with
          the redesigned tinted card showing the chosen plan icon, the
          users/cycle line, the prominent "Amount to pay today", and a
          single "Change plan" link. The Custom-Plan-applied detailed
          panel above already has its own "Change plan" affordance, so
          this card only renders for Trial / Standard quotes. */}
      {quote && !(quote.customPlanApplied || quote.couponApplied) ? (
        <div className="rounded-2xl border border-brand-200 bg-brand-50/40 px-4 py-3 mb-3 min-w-0">
          {/* Row 1: icon + plan info, with Change plan link on the right */}
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white shrink-0"
              aria-hidden="true"
            >
              {isTrialQuote ? <LeafIcon className="h-5 w-5" /> : <TeamOutlined />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-ink-900 m-0 leading-tight">
                Selected plan
              </p>
              {isTrialQuote ? (
                <p className="text-xs text-ink-600 m-0 mt-0.5 truncate">
                  {quote.planName || TRIAL_PLAN.planName} ·{' '}
                  {quote.trialDays || TRIAL_PLAN.trialDays} days ·{' '}
                  {quote.includedUsers || TRIAL_PLAN.includedUsers} users
                </p>
              ) : (
                <p className="text-xs text-ink-600 m-0 mt-0.5 truncate">
                  {getSignupState().userCount}{' '}
                  user{getSignupState().userCount === 1 ? '' : 's'}
                  {' · '}
                  {getSignupState().billingCycle === 'annual'
                    ? 'Annual billing'
                    : 'Monthly billing'}
                </p>
              )}
            </div>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={handleBackToPlans}
              disabled={isActive}
              className="!px-0 !font-semibold !text-brand-600 hover:!text-brand-700 shrink-0"
            >
              Change
            </Button>
          </div>
          {/* Row 2: Amount to pay today */}
          <div className="mt-2 pt-2 border-t border-brand-200 flex items-baseline justify-between gap-3 min-w-0">
            <span className="text-xs text-ink-600 whitespace-nowrap">
              Amount to pay today
            </span>
            <span className="text-xl sm:text-2xl font-bold text-brand-700 tabular-nums whitespace-nowrap">
              {paiseToRupees(quote.totalPaise)}
            </span>
          </div>
        </div>
      ) : null}

      {/* MASTER CREDENTIALS + PAY */}
      {quote ? (
        <>
          {/* Info banner explaining the @master.glass-i email pattern */}
          <div className="rounded-2xl border border-brand-200 bg-brand-50/60 px-4 py-2.5 flex items-start gap-3 mb-3">
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white border border-brand-200 text-brand-500 shrink-0"
              aria-hidden="true"
            >
              <InfoCircleOutlined className="text-base" />
            </span>
            <div className="min-w-0 text-sm">
              <p className="font-semibold m-0 text-brand-700 leading-tight">
                Create your master account
              </p>
              <p className="m-0 mt-0.5 text-ink-700 leading-snug">
                Your login email will be{' '}
                <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-brand-100">
                  {'{username}'}@master.glass-i
                </code>
                . This is the primary admin account for your company.
              </p>
            </div>
          </div>

          <Form
            form={credForm}
            layout="vertical"
            onFinish={handlePay}
            onFinishFailed={handlePayValidationFailed}
            disabled={isActive}
            size="large"
            className="onboarding-compact-form"
            // username is safely recovered from sessionStorage on refresh.
            // password is intentionally absent — the user must re-enter
            // it on every fresh form mount.
            initialValues={{
              masterUsername: recoveredUsername || ''
            }}
          >
            {/* Credentials card */}
            <div className="rounded-2xl border border-surface-200 bg-white p-4 mb-3">
              <Form.Item
                label={
                  <span className="text-sm font-semibold text-ink-800 inline-flex items-center gap-2 flex-wrap">
                    Master username
                    <span className="text-xs font-normal text-ink-500">
                      3–30 characters · lowercase letters, numbers, dot,
                      dash, underscore
                    </span>
                  </span>
                }
                name="masterUsername"
                rules={[
                  { required: true, message: 'username is required' },
                  {
                    validator: (_r, v) =>
                      !v || isMasterUsername(String(v).toLowerCase())
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              'use 3–30 lowercase letters, digits, dot, dash, or underscore; must start with a letter'
                            )
                          )
                  }
                ]}
              >
                <Input
                  prefix={<UserOutlined className="text-ink-400" />}
                  placeholder="ravi"
                  autoComplete="username"
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-sm font-semibold text-ink-800">
                    Master password
                  </span>
                }
                name="masterPassword"
                rules={[
                  { required: true, message: 'password is required' },
                  {
                    validator: (_r, v) => {
                      const s = passwordStrength(v || '');
                      return s.ok
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error('needs ' + s.reasons.join(', '))
                          );
                    }
                  }
                ]}
                className="!mb-2"
              >
                <Input.Password
                  prefix={<LockOutlined className="text-ink-400" />}
                  autoComplete="new-password"
                />
              </Form.Item>

              {/* Live password requirement chips. Display-only —
                  passwordStrength() above is the actual gate. */}
              {(() => {
                const pwd = String(watchedMasterPassword || '');
                const rules = [
                  { label: '12+ characters', ok: pwd.length >= 12 },
                  { label: 'Uppercase', ok: /[A-Z]/.test(pwd) },
                  { label: 'Lowercase', ok: /[a-z]/.test(pwd) },
                  { label: 'Number', ok: /[0-9]/.test(pwd) },
                  { label: 'Symbol', ok: /[^A-Za-z0-9]/.test(pwd) }
                ];
                return (
                  <div className="flex flex-wrap gap-2">
                    {rules.map((rule) => (
                      <span
                        key={rule.label}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                          rule.ok
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-surface-200 bg-white text-ink-500'
                        }`}
                      >
                        <CheckCircleOutlined
                          className={
                            rule.ok ? 'text-emerald-600' : 'text-ink-300'
                          }
                        />
                        {rule.label}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Security strip — green tinted, reassures the user about
                payment verification + tab-close behaviour. */}
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-2.5 flex items-start gap-3 mb-3">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 shrink-0"
                aria-hidden="true"
              >
                <SafetyOutlined className="text-base" />
              </span>
              <div className="text-sm min-w-0">
                <p className="font-semibold m-0 text-ink-800 leading-tight">
                  Payment is verified securely before your account is created.
                </p>
                <p className="m-0 mt-0.5 text-ink-600 text-xs leading-snug">
                  Do not close this tab until verification is complete.
                </p>
              </div>
            </div>

            {/* Bottom action row — Back link on the left, full-width
                Pay button on the right. */}
            <div className="pt-3 border-t border-surface-200 flex flex-wrap items-center gap-3">
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToPlans}
                disabled={isActive}
                className="!px-0 !font-semibold !text-brand-600 hover:!text-brand-700"
              >
                Back
              </Button>
              <Form.Item className="!mb-0 flex-1 min-w-[240px]">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={
                    phase === 'creating_order' ||
                    phase === 'checkout_opened' ||
                    phase === 'verification_pending' ||
                    phase === 'verification_slow'
                  }
                  // Disabled ONLY in busy phases or when prerequisites are
                  // truly missing (no pendingSignupId / no quote). We do
                  // NOT disable on empty-looking password — Form rules +
                  // onFinishFailed handle that case with a popup, which
                  // also covers browser-autofill races where the value is
                  // present but Form.useWatch hasn't observed it yet.
                  disabled={isActive || !pendingSignupId || !quote}
                  icon={
                    phase === 'verified' || phase === 'completed' ? (
                      <CheckCircleOutlined />
                    ) : (
                      <CreditCardOutlined />
                    )
                  }
                  block
                  className="!h-12 !text-base !font-semibold"
                >
                  {(() => {
                    // Stage 5K-CustomPlan: every public plan now requires
                    // payment, so there is no "Create account" no-payment
                    // fallback. Both the trial and the custom plan flow
                    // through the same Razorpay path with this label.
                    const totalLabel = paiseToRupees(quote.totalPaise);
                    const accountLabel = isTrialQuote
                      ? 'create trial account'
                      : 'create account';
                    const prefix = order && order.mode === 'mock' ? 'Pay (mock) ' : 'Pay ';
                    return `${prefix}${totalLabel} & ${accountLabel}`;
                  })()}
                </Button>
              </Form.Item>
            </div>

            <p className="m-0 mt-2 text-xs text-ink-400">
              If the browser closes before payment, no amount will be deducted.
              You can restart from this page.
            </p>
          </Form>
        </>
      ) : null}
    </div>
  );
}
